<script>
//Repositories
import Repositories from '@/repositories';
const WorkOrdersRepository = Repositories.get('workorders');

//Mixins
import textMixin from '@/mixins/textMixin';
import dateMixin from '@/mixins/dateMixin';
import statusMixin from '@/mixins/statusMixin';

//Components
import GridComponent from '@/components/widgets/grids/api-linked-grid';
import StatusComponent from '@/components/widgets/badges/status';
import DeleteButton from '@/components/widgets/buttons/delete-button';

//Modals
import ModalLayout from '@/router/layouts/modal-view.vue';
import WorkOrderInfoModal from '../../management/main.vue';

export default {
  props: {
    filters: Object,
    fields: Array,
  },
  components: {
    GridComponent,
    StatusComponent,
    ModalLayout,
    WorkOrderInfoModal,
    DeleteButton,
  },
  mixins: [textMixin, dateMixin, statusMixin],
  data() {
    return {
      workOrdersList: [],
      fullListSize: 0,
      gridContext: null,
      isLoading: false,
      workOrderForModal: null,
      lastClickedWorkOrder: null,
    };
  },
  created() {
    this.gridContext = {
      currentPage: 1,
      perPage: this.itemsPerPage,
      sortBy: 'statusOrder',
      sortDesc: false,
    };
    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      WorkOrdersRepository.getAll({ ...this.filters, ...this.gridContext })
        .then(({ data }) => {
          if (data != null && data.data != null) {
            this.workOrdersList = data.data.map((x) => {
              return this.addExtraFields(x);
            });
            this.fullListSize = data.count;
          }
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => (this.isLoading = false));
    },
    consumeWorkOrder(workOrder) {
      if (workOrder.reservedItemsQuantity > 0) {
        this.isLoading = true;

        WorkOrdersRepository.actions
          .consume(workOrder.idPublic)
          .then(() => {
            this.loadData();
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));
            this.isLoading = false;
          });
      }
    },
    workOrderUpdated(workOrder) {
      let foundIndex = this.workOrdersList.findIndex(
        (x) => x.idPublic == workOrder.idPublic
      );

      this.workOrdersList = [
        ...this.workOrdersList.slice(0, foundIndex),
        workOrder,
        ...this.workOrdersList.slice(foundIndex + 1),
      ];

      this.workOrderForModal = workOrder;
    },
    deleteWorkOrder(workOrder) {
      workOrder.isLoading = true;
      WorkOrdersRepository.delete(workOrder.idPublic)
        .then(() => {
          let foundIndex = this.workOrdersList.findIndex(
            (x) => x.idPublic == workOrder.idPublic
          );
          this.workOrdersList.splice(foundIndex, 1);
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
          workOrder.deleteError = true;
          workOrder.isLoading = false;
        });
    },
    contextChanged(context) {
      this.gridContext = context;
      this.loadData();
    },
    openModal(workOrder, modalName) {
      this.workOrderForModal = workOrder;
      this.lastClickedOrder = modalName + '-' + workOrder.idPublic;
      this.$bvModal.show(modalName);
    },
    modalClosed() {
      this.workOrderForModal = null;
      this.loadData();
    },
    addExtraFields(item) {
      item.isLoading = false;
      item.deleteError = false;
      return item;
    },
  },
  computed: {
    currencyFormatter() {
      return this.$store.state.cache.currencyFormatter;
    },
    workOrdersListComputed() {
      return this.workOrdersList.map((x) => {
        let status = this.getStatusFromCode(x.status, 'WorkOrders');
        return { ...x, ...status };
      });
    },
    itemsPerPage() {
      return this.$store.getters['localstorage/getDynamicConfigs'](
        'gridConfig-work-orders-grid'
      ).itemsPerPage;
    },
  },
  watch: {
    filters() {
      this.workOrdersList = [];
      this.gridContext.currentPage = 1;
      this.loadData();
    },
  },
};
</script>

<template>
  <b-card no-body class="p-3 custom-inventory-shadow">
    <GridComponent
      v-if="gridContext != null"
      id="work-orders-grid"
      :fields="fields"
      :items="workOrdersListComputed"
      :is-busy="isLoading"
      :nbItems="fullListSize"
      :default-sort="gridContext.sortBy"
      :current-page="gridContext.currentPage"
      @context-changed="contextChanged"
    >
      <template #cell(name)="data">
        <span
          v-html="
            highlightMatches(
              data.item.name,
              filters != null ? filters.search : ''
            )
          "
        ></span>
      </template>

      <template #cell(statusOrder)="data">
        <div>
          <StatusComponent
            :text="data.item.statusName"
            :variant="data.item.statusVariant"
          />
        </div>
      </template>

      <template #cell(totalItemsPrice)="data">
        {{ currencyFormatter.format(data.item.totalItemsPrice) }}
      </template>

      <template #cell(reservedTotal)="data">
        {{ data.item.reservedItemsQuantity }} /
        {{ data.item.totalItemsQuantity }}
      </template>
      <template #cell(deliveryDate)="data">
        {{ formatDate(data.item.deliveryDate) }}
      </template>
      <template #cell(consume)="data">
        <span
          class="table-row-relative"
          @click="consumeWorkOrder(data.item)"
          :style="
            'font-size: 1.3em; ' + data.item.reservedItemsQuantity > 0
              ? 'cursor: pointer;'
              : ''
          "
        >
          <i
            class="fas fa-check"
            :class="
              data.item.reservedItemsQuantity > 0
                ? 'text-success'
                : 'text-secondary'
            "
            :style="
              data.item.reservedItemsQuantity > 0 > 0 ? 'cursor: pointer' : ''
            "
          />
        </span>
      </template>

      <template #cell(manage)="data">
        <div
          @click.capture.prevent="openModal(data.item, 'work-order-info-modal')"
        >
          <b-link
            :to="{
              name: 'manageWorkOrder',
              params: { idPublic: data.item.idPublic },
            }"
          >
            <i
              v-if="
                lastClickedWorkOrder ===
                  'work-order-info-modal-' + data.item.idPublic
              "
              class="fas fa-eye pl-2 pr-2"
              style="cursor: pointer; font-size: 1.3em; color: #20976e"
            ></i>
            <i
              v-else
              class="fas fa-eye text-success pl-2 pr-2"
              style="cursor: pointer; font-size: 1.3em"
            ></i>
          </b-link>
        </div>
      </template>

      <template #cell(delete)="data">
        <DeleteButton
          :key="data.item.idPublic"
          :error="!data.item.isLoading && data.item.deleteError"
          @clicked="deleteWorkOrder(data.item)"
        />
      </template>
    </GridComponent>

    <ModalLayout
      name="work-order-info-modal"
      @modal-closed="modalClosed"
      :title="
        $t('workOrders.workOrder') +
          ' ' +
          (workOrderForModal != null ? workOrderForModal.name : '')
      "
      :route="{
        name: 'manageWorkOrder',
        params: {
          idPublic: workOrderForModal != null ? workOrderForModal.idPublic : '',
        },
      }"
    >
      <div v-if="workOrderForModal != null">
        <WorkOrderInfoModal
          :workOrderIdPublic="workOrderForModal.idPublic"
          @work-order-updated="workOrderUpdated"
        />
      </div>
    </ModalLayout>
  </b-card>
</template>
